/* eslint-disable indent */
/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, StatusBar, Animated, Dimensions } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import DeviceTimeFormat from 'react-native-device-time-format';
import { initialWindowMetrics } from 'react-native-safe-area-context';

import {
  isIOS,
  isWeb,
  isPhone,
  ROLES,
  safeAreaInsetsBottom,
  safeAreaInsetsTop,
  isAndroid,
  setSecureValue,
  getSecureValue,
  removeSecureValue,
} from '../helpers';
import { getStatusBarHeight } from 'react-native-status-bar-height';
import ColorWrapper from '@styles/colors';

export const isBottomOffset = safeAreaInsetsBottom > 0;

export let scaleFactor = 0,
  scaleFactorWebPanel = 0,
  fontScale = 0,
  height = 0,
  fullHeight = 0,
  width = 0,
  fullWidth = 0,
  topOffsetForPosition = 0,
  ratio = 0,
  base = { w: 0, h: 0 },
  baseDesign = { width: 0, height: 0 },
  stretchDesign = { width: 0, height: 0 },
  isChromeOS = false,
  notchTop = 0,
  webHeader = isPhone ? 40 : 44,
  gcd = 0,
  maxWebWidth = 1440,
  notchBottom = 0,
  role = null,
  env = null,
  featureEnv = null,
  isAdmin = false,
  isManager = false,
  isOperator = false,
  bottomBarHeight = isPhone ? (isAndroid ? 56 + (initialWindowMetrics?.insets?.bottom || 32) : isBottomOffset ? 77 : 56) : isBottomOffset ? 68 : 50,
  is24HourFormat = false,
  selectedZoomOPIndex = 0;

let statusBarSize = getStatusBarHeight();

export let scale = (n) => n;
export let scaleWeb = (n) => n;
export let scaleOP = (n) => n;
export let getMaxOPZoom = () => 1;

export const setSelectedZoomOPIndex = (i) => {
  selectedZoomOPIndex = i;
};

export let stretchFactor = {};

export const setConfig = async (w, h, zoomOPIndex) => {
  fullHeight = h;

  isChromeOS = isIOS ? false : await DeviceInfo.hasSystemFeature('org.chromium.arc.device_management');

  const topOffsetForPosition = (() => {
    if (isWeb) return 0;
    if (isChromeOS) return 40;
    if (!isIOS) {
      const isEdgeToEdge = (initialWindowMetrics?.insets?.top || 0) > 0;
      return isEdgeToEdge ? 0 : StatusBar.currentHeight || 0;
    }
    return 0;
  })();

  notchTop = (() => {
    if (isAndroid) {
      // For Android, use dynamic safe area insets to get the status bar height
      const dynamicSafeAreaTop = initialWindowMetrics?.insets?.top || 0;
      return dynamicSafeAreaTop;
    }
    const notch = DeviceInfo.hasNotch();
    const dynamicIsland = DeviceInfo.hasDynamicIsland();
    if (notch) {
      if (isIOS && isPhone) {
        return safeAreaInsetsTop - 14;
      } else {
        return statusBarSize;
      }
    }
    return 0;
  })();

  // For Android bottom navigation, we'll set it after async operations
  notchBottom = 0;

  if (isWeb) {
    height = h;
    width = w;
  } else {
    height = h + topOffsetForPosition - notchTop - notchBottom;
    width = w;
  }

  fullWidth = Dimensions.get('window').width;

  const [shortDimension, longDimension] = width < height ? [width, height] : [height, width];

  const gcdFn = (a, b) => {
    if (!longDimension || !shortDimension) {
      return 1;
    }
    return b == 0 ? a : gcdFn(b, a % b);
  };

  const r = gcdFn(longDimension, shortDimension);

  gcd = r;

  ratio = (longDimension / r / (shortDimension / r)).toFixed(2);

  baseDesign = (() => {
    if (isWeb) {
      return {
        width: 1080,
        height: 720,
      };
    }
    const tablet = {
      width: 1080,
      height: 720,
    };
    const phone = {
      width: 360,
      height: 640,
    };
    return isPhone ? phone : tablet;
  })();

  stretchDesign = (() => {
    if (isWeb) {
      if (ratio == 1.6) {
        return {
          width: 1152,
          height: 720,
        };
      }

      if (ratio == 1.63) {
        return {
          width: 1171,
          height: 720,
        };
      }
      return {
        width: 1280,
        height: 720,
      };
    }
  })();

  fontScale = Dimensions.get('window').fontScale.toFixed(2) * 1.03;

  stretchFactor = {
    horizontal: Math.max(1, isWeb ? width / stretchDesign.width : width / 375 / fontScale).toFixed(4),
  };

  scaleFactor = (() => {
    if (isWeb) {
      return 1;
    }

    return Math.min(width / baseDesign.width, height / baseDesign.height, width / (360 * stretchFactor.horizontal));
  })();

  stretchFactor.vertical = (height / scaleFactor / 640).toFixed(4);

  scaleFactorWebPanel = (() => {
    if (isWeb) {
      return Math.min(width / baseDesign.width, height / baseDesign.height, width / (360 * stretchFactor.horizontal));
    }
  })();

  base = (() => {
    return {
      w: Math.ceil(baseDesign.width * stretchFactor.horizontal),
      h: Math.ceil(baseDesign.height * stretchFactor.vertical),
    };
  })();

  webHeader = isPhone ? 40 : 44;
  if (isWeb) {
    height = height - webHeader;
    if (width > maxWebWidth) {
      width = maxWebWidth;
    }
  }

  scale = (value, round = true) => {
    'worklet'; // this required to works with Reanimated
    const factor = isWeb ? 1 : scaleFactor;
    return round ? Math.round(value * factor) : value * factor;
  };

  scaleWeb = (value, round = false) => {
    'worklet'; // this required to works with Reanimated
    const factor = isWeb ? scaleFactorWebPanel : scaleFactor;
    return round ? Math.round(value * factor) : value * factor;
  };

  const scaleFactorOPObject = (() => {
    const baseDesignWidth = 1080;
    const baseDesignHeight = 720;
    const baseRatio = 1.5;
    const scaleSteps = 7;

    setSelectedZoomOPIndex(zoomOPIndex);

    getMaxOPZoom = () => {
      const determination = ratio < baseRatio ? 'Width' : ratio > 1.5 ? 'Height' : 'Both';
      return (determination === 'Width' ? fullWidth / baseDesignWidth : fullHeight / baseDesignHeight) - 1;
    };

    const maxZoom = getMaxOPZoom();
    let result = {};

    for (let i = 0; i <= scaleSteps - 1; i++) {
      const r = (i / (scaleSteps - 1)) * maxZoom + 1;
      result[i] = Math.round(r * 100) / 100;
    }

    return result;
  })();

  scaleOP = (value, shouldRound = true) => {
    'worklet';
    return scaleFactorOPObject[selectedZoomOPIndex] * scale(value, shouldRound);
  };

  // Only calculate notchBottom once to prevent flickering during navigation
  if (notchBottom === 0) {
    if (isAndroid && isPhone) {
      const dynamicBottomInset = initialWindowMetrics?.insets?.bottom;
      if (dynamicBottomInset && dynamicBottomInset > 0) {
        notchBottom = dynamicBottomInset;
      } else {
        notchBottom = 32;
      }
    } else {
      const notch = DeviceInfo.hasNotch();
      if (notch && isIOS && isPhone) {
        notchBottom = 22;
      }
    }
  }

  await initRole();

  if (isWeb) {
    const list12Format = [
      'Africa/Cairo',
      'Asia/Dhaka',
      'Asia/Calcutta',
      'Asia/Manila',
      'Asia/Jakarta',
      'Asia/Manila',
      'Asia/Kuala_Lumpur',
      'Asia/Riyadh',
      'America/Tegucigalpa',
      'America/Managua',
      'Europe/Dublin',
      'America/Toronto',
      'America/Mexico_City',
      'America/New_York',
      'Australia/Sydney',
      'Pacific/Auckland',
      'America/Havana',
    ];

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    is24HourFormat = !list12Format.includes(timeZone);
  } else {
    is24HourFormat = await DeviceTimeFormat.is24HourFormat();
  }
};

export const updateFeatureEnv = async (e) => {
  featureEnv = e;
  setSecureValue('featureEnv', e);
};

export const initRole = async (userRole) => {
  const reset = () => {
    role = null;
    isAdmin = false;
    isManager = false;
    isOperator = false;
  };

  try {
    env = getSecureValue('env');
    featureEnv = getSecureValue('featureEnv');

    if (getSecureValue('devRoleOverride')) removeSecureValue('devRoleOverride');

    if (userRole) {
      setSecureValue('role', userRole || '');
    }
    const storageRole = userRole || getSecureValue('role');
    if (storageRole) {
      role = storageRole;
      isAdmin = role === ROLES.ADMINISTRATOR;
      isManager = role === ROLES.MANAGER;
      isOperator = role === ROLES.OPERATOR;
      return;
    }
    reset();
  } catch (e) {
    console.log('Error to detect user role', e);
    reset();
  }
};

export const ElementBox = (props) => {
  const { children } = props;
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        scaleFactor,
        scale,
        height,
        width,
        topOffsetForPosition,
      });
    }
    return child;
  });

  return (
    <View
      style={{
        flex: 1,
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {childrenWithProps}
    </View>
  );
};

const BaseForAnimation = (props) => {
  const { animated, children } = props;

  return animated ? <Animated.View {...props}>{children}</Animated.View> : <View {...props}>{children}</View>;
};

export const Element = (props) => {
  const { config: configTablet, configMobile, children, skipPriority = false, scale: scaleFromProps } = props;
  const priority = isPhone ? [skipPriority ? {} : configTablet, configMobile] : [{}, configTablet];

  const {
    position = {},
    animated = false,
    width: elWidth = null,
    height: elHeight = null,
    verticalCenter = null,
    horizontalCenter = null,
    customPosition = null,
    parentWidth = width,
    parentHeight = height,
    border = null,
    ...style
  } = {
    ...priority[0],
    ...priority[1],
  };
  const pos = {};

  const s = scaleFromProps || scale;

  Object.keys(position).map((item) => {
    if (typeof position[item] === 'number') {
      pos[item] = s(position[item]);
    } else {
      pos[item] = position[item];
    }
  });

  const stl = [
    {
      position: customPosition ? customPosition : 'absolute',
      ...pos,
      ...style,
    },
    elWidth !== null && {
      width: typeof elWidth === 'number' ? s(elWidth) : elWidth,
    },
    elHeight !== null && {
      height: typeof elHeight === 'number' ? s(elHeight) : elHeight,
    },
    verticalCenter !== null && {
      top: parentHeight / 2 - s(elHeight) / 2 + s(verticalCenter),
    },
    horizontalCenter !== null && {
      left: parentWidth / 2 - s(elWidth) / 2 + s(horizontalCenter),
    },
  ];

  return [
    border && <BaseForAnimation {...{ animated }} key={`${JSON.stringify(configTablet)} + border`} style={[...stl, border]} />,
    <BaseForAnimation {...{ animated }} key={`${JSON.stringify(configTablet)} - main`} style={stl}>
      {children}
    </BaseForAnimation>,
  ];
};

export const countElementSize = (position) => {
  const scaled = {};
  Object.keys(position).map((item) => {
    if (typeof position[item] === 'number') {
      scaled[item] = scale(position[item]);
    } else {
      const base = (() => {
        switch (item) {
          case 'top':
            return height;
          case 'bottom':
            return height;
          case 'left':
            return width;
          case 'right':
            return width;
        }
      })();
      scaled[item] = (parseFloat(position[item]) * base) / 100;
    }
  });

  const h = height - scaled.top - scaled.bottom;
  const w = width - scaled.left - scaled.right;

  return { w, h };
};

const WebWrapper = (props) => {
  const { C, children, backgroundColor, h = height, webWrapperContent } = props;

  if (isWeb) {
    return (
      <View
        style={{
          width: '100%',
          height: h,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: backgroundColor,
        }}
      >
        {webWrapperContent && webWrapperContent()}
        {children}
      </View>
    );
  }
  return children;
};

const Grid = (props) => {
  let {
    customWidth,
    customHeight,
    horizontalLine = 0,
    verticalsLine = 0,
    horizontalSpace = 0,
    verticalSpace = 0,
    horizontalEdgeTop = 0,
    horizontalEdgeBottom = 0,
    verticalEdge = 0,
    children,
    backgroundColor,
    headerOffset = false,
    notchOffset = false,
    sliderOffset = false,
    noHeight = false,
    webWrapperContent = null,
    C,
    useScaleWeb,
    useScaleOP,
  } = props;

  const s = useScaleWeb ? scaleWeb : useScaleOP ? scaleOP : scale;

  const w = customWidth || width;
  const h = customHeight || height;

  horizontalEdgeTop = s(horizontalEdgeTop);
  horizontalEdgeBottom = s(horizontalEdgeBottom);
  horizontalSpace = s(horizontalSpace);
  verticalEdge = s(verticalEdge);
  verticalSpace = s(verticalSpace);

  const paddingTop = (notchOffset ? notchTop : 0) + horizontalEdgeTop || 0;
  const paddingBottom = (sliderOffset ? notchBottom : 0) + horizontalEdgeBottom || 0;

  const vertical = (w - verticalEdge * 2 - verticalSpace * (verticalsLine - 1)) / verticalsLine;

  const horizontal = (h - (paddingTop + paddingBottom) - horizontalSpace * (horizontalLine - 1)) / horizontalLine;

  const combineVertical = (count) => vertical * count + verticalSpace * (count - 1);
  const combineHorizontal = (count) => horizontal * count + horizontalSpace * (count - 1);

  return (
    <WebWrapper {...{ C, backgroundColor, w, h, webWrapperContent }}>
      <View
        style={[
          {
            flex: 1,
            width: w,
            paddingTop: paddingTop,
            paddingBottom: paddingBottom,
            paddingLeft: verticalEdge || 0,
            paddingRight: verticalEdge || 0,
            backgroundColor,
            marginTop: headerOffset && !isIOS ? -topOffsetForPosition : 0,
          },
          !noHeight && { height: h },
        ]}
      >
        {React.cloneElement(children, {
          combineVertical,
          combineHorizontal,
          vertical,
          horizontal,
          scaleFactor,
          scale: s,
          height: h,
          width: w,
          topOffsetForPosition,
          notchBottom,
          notchTop,
          ratio,
          base,
        })}
      </View>
    </WebWrapper>
  );
};

export const GridBox = ColorWrapper(Grid);
